import React, { useEffect, useState } from "react";
import { useContext } from "react";
import { CartContext } from "../../context/cart.context";
import { UserContext } from "../../context/user.context";
import { CallCenterAPI } from "../../utils/callcenterAPI";
import geoData from "../../utils/Data/GeoLocations.json";
import { NetworkInt } from "../../utils/NetworkInt";
import { Link, useNavigate } from "react-router-dom";

import CheckoutItemCard from "./CheckoutItemCard";
import { MosqueSelection } from "./MosqueSelection";
import { getProductsByPriceListCode } from "../../utils/ProductsApi";
import LoadingSpinner from "../../components/loader/Myloader";
import { ShoppingBagIcon } from "@heroicons/react/24/outline";
import CheckoutDL from "../../components/deliveryLoctions/CheckoutDL";
import { getDeliveryLocations } from "../../utils/zulalWebAPI";
import { UserPlusIcon } from "@heroicons/react/20/solid";

import { UilUserLocation } from "@iconscout/react-unicons";
import UiMosque from "../../utils/icons/UiMosque";

export const CheckOut = () => {
  const {
    cartItems, // always address prices!
    shippingCharges,
    cartGTotal,
    cartCount,
    cartTotal,
  } = useContext(CartContext);
  const { currentUser, sonicAccessToken } = useContext(UserContext);

  const [deliveryOption, setDeliveryOption] = useState("delivery");
  const [mosquePrices, setMosquePrices] = useState({}); // { [itemId]: price }
  const [loading, setLoading] = useState(false);
  const [selectedMosque, setSelectedMosque] = useState({});
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locations, setLocations] = useState([]);
  const [userCityName, setUserCityName] = useState("");
  const [userAddress, setUserAddress] = useState("");
  const [merchantDefinedData, setMerchantDefinedData] = useState([]);
  const [fiveGallonNotice, setFiveGallonNotice] = useState(false);
  const [donateWater, setDonateWater] = useState(false);

  const navigate = useNavigate();

  // Fetch delivery addresses
  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const customerCode = currentUser.customerCode;
        const response = await getDeliveryLocations(customerCode);
        setLocations(response.data || []); // Update addresses with the fetched data

        // const response = await getDeliveryLocations(currentUser);
        // console.log("response from getDeliveryLocations:", response);
        // setLocations(response.data);
      } catch (error) {
        console.error("Error fetching addresses:", error);
      }
    };
    fetchAddresses();
    // eslint-disable-next-line
  }, []);

  // Auto-select default location
  useEffect(() => {
    if (locations && locations.length > 0) {
      //console.log(locations);
      const defaultLoc = locations.find((loc) => loc.isDefault);
      setSelectedLocation(defaultLoc || locations[0]);
      setDeliveryOption("delivery");
    }
  }, [locations]);

  // Fetch mosque prices when needed
  useEffect(() => {
    if (deliveryOption === "mosque") {
      setLoading(true);
      getProductsByPriceListCode({
        listCode: "sm",
        sonicToken: sonicAccessToken,
      })
        .then((response) => {
          const priceMap = {};
          (response.data?.itemDetails || []).forEach((item) => {
            priceMap[item.itemCode.trim()] = item.price;
          });
          setMosquePrices(priceMap);
        })
        .finally(() => setLoading(false));
      setDonateWater(true);
    } else {
      setMosquePrices({});
      setDonateWater(false);
    }
    // eslint-disable-next-line
  }, [deliveryOption, sonicAccessToken]);

  // Utility to get display price
  const getDisplayPrice = (item) =>
    deliveryOption === "mosque" && mosquePrices[item.id]
      ? `${mosquePrices[item.id]} AED`
      : item.price;

  // Five gallon notice
  useEffect(() => {
    setFiveGallonNotice(cartItems.some((item) => item.id === "9000464"));
  }, [cartItems]);

  // Merchant data for order
  useEffect(() => {
    let result = {};
    result["customerCode"] = `${currentUser.customerCode}`;
    result["orderSource"] = "WEB";
    cartItems.forEach((item, index) => {
      result[`item${index}`] = `id: ${item.id}, uom: ${
        item.uom
      },price: ${getDisplayPrice(item)}, quantity: ${
        item.quantity
      }, discount: ${item.discount}`;
    });
    if (deliveryOption === "mosque") {
      result["donateWater"] = "true";
      result["MosqueName"] = selectedMosque.MosqueName;
      result["MosqueID"] = selectedMosque.MosqueID;
    } else {
      result["donateWater"] = "false";
    }
    setMerchantDefinedData(result);
    // eslint-disable-next-line
  }, [cartItems, selectedMosque, deliveryOption, mosquePrices]);

  // City/address for billing
  useEffect(() => {
    async function getCustomerDetails() {
      try {
        const objCustomerDetails = await CallCenterAPI().getCustomerDetails({
          customerToken: sonicAccessToken,
        });
        const userDetails = objCustomerDetails.data;
        if (userDetails.addresses?.[0]?.address)
          setUserAddress(userDetails.addresses[0].address);
        const foundCity = geoData.cities.find(
          (city) =>
            city.CityID === userDetails.cityID &&
            city.StateID === userDetails.stateID
        );
        setUserCityName(foundCity ? foundCity.CityName : "NA");
      } catch (error) {
        // ignore
      }
    }
    if (sonicAccessToken) getCustomerDetails();
  }, [sonicAccessToken]);

  // Location select
  const handleLocationSelect = (location) => setSelectedLocation(location);

  // Mosque select
  const handleMosqueSelection = (mosque) => setSelectedMosque(mosque);

  // Delivery option change
  const handleDeliveryOptionChange = (e) => {
    const val = e.target.value;
    setDeliveryOption(val);
    if (val === "mosque") setSelectedLocation(null);
    if (val === "delivery") setSelectedMosque({});
  };

  // Display price for calculations (removes ' AED')
  const getRawPrice = (item) => {
    const price =
      deliveryOption === "mosque" && mosquePrices[item.id]
        ? mosquePrices[item.id]
        : item.price;
    return parseFloat(String(price).replace(/[^\d.]/g, ""));
  };

  // Calculations (tax, total, etc.)
  function multipleOfValues(val1, val2) {
    const total = parseFloat(val1) * parseFloat(val2);
    return total.toFixed(2);
  }
  function addValues(val1, val2) {
    const total = parseFloat(val1) + parseFloat(val2);
    return total.toFixed(2);
  }
  function calculateTaxAmount(price, quantity, amount) {
    const itemvalue = parseFloat(price) * parseInt(quantity);
    const t = parseFloat(itemvalue) * amount;
    return parseFloat(t);
  }
  function CalculateMinorValues() {
    let grandTotal = 0;
    cartItems.forEach((cartItem) => {
      const price = getRawPrice(cartItem) * cartItem.quantity;
      const taxAmount = calculateTaxAmount(
        getRawPrice(cartItem),
        cartItem.quantity,
        0.05
      );
      const itemValueWithTax = addValues(
        Math.round(price * 100) / 100,
        Math.round(taxAmount * 100) / 100
      );
      grandTotal = parseFloat(addValues(grandTotal, itemValueWithTax));
    });
    return Math.round(grandTotal * 100);
  }

  // Order submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser.customerCode) {
      alert("Please Login again to Continue, we encountered a problem !!!");
      navigate("/user/dashboard", { state: { PageTitle: "Dashboard" } });
      return;
    }
    if (
      deliveryOption === "mosque" &&
      (!selectedMosque.MosqueID || !selectedMosque.MosqueName)
    ) {
      alert("Please select the Mosque !!!");
      return;
    }
    if (
      deliveryOption === "delivery" &&
      (!selectedLocation || !selectedLocation.locationLabel)
    ) {
      alert("Please select the delivery location");
      return;
    }
    if (!currentUser.email) {
      alert("please update the email address!!!");
      navigate("/user/profile", { state: { PageTitle: "Profile" } });
      return;
    }
    if (deliveryOption === "mosque") {
      merchantDefinedData.zdeliveryLocation =
        "Mosque Delivery, " +
        selectedMosque.MosqueName +
        " - " +
        selectedMosque.MosqueID;
    } else if (deliveryOption === "delivery") {
      merchantDefinedData.zdeliveryLocation =
        selectedLocation.locationLabel +
        ", " +
        selectedLocation.flatNumber +
        ", " +
        selectedLocation.buildingName +
        ", " +
        selectedLocation.areaName +
        ", " +
        selectedLocation.cityName;
    }
    const env_var = process.env.NODE_ENV;
    const userOrder = {
      action: "PURCHASE",
      amount: {
        currencyCode: "AED",
        value: CalculateMinorValues(),
      },
      emailAddress: currentUser.email,
      language: "en",
      merchantAttributes: {
        redirectUrl:
          env_var === "development"
            ? "https://qa.zulal.ae:3000/orderconfirmation"
            : "https://www.zulal.ae/orderconfirmation",
        skipConfirmationPage: "true",
        merchantOrderReference: currentUser.customerCode,
      },
      billingAddress: {
        firstName: currentUser.outletName?.split(" ")[0] || "",
        lastName: currentUser.outletName?.split(" ")[1] || "",
        address1: userAddress,
        city: userCityName,
        countryCode: "UAE",
      },
      merchantDefinedData,
    };
    const tokenResponse = NetworkInt().getAccessToken();
    tokenResponse
      .then((response) => {
        const accessToken = response.access_token;
        const params = { accessToken: accessToken, userOrder: userOrder };
        NetworkInt()
          .createOrder(params)
          .then((response) => {
            alert("We will redirect you to Payment Gateway Page");
            window.open(response.data.payByLinkUrl, "_self", "noreferrer");
          })
          .catch(() => {
            alert("Error placing order. Please try again.");
          });
      })
      .catch(() => {
        alert("Unable to get Token from NI API. Please try again.");
      });
  };

  return (
    <div>
      <div className="mx-auto mr-4 ml-4 mb-28">
        <div className="fixed flex justify-center items-center h-screen">
          {loading && <LoadingSpinner />}
        </div>
        <div className="">
          <span className="text-navarbarText text-3xl font-poppins font-medium mb-8">
            CheckOut - Order Summary
          </span>

          <div className="mt-5 p-6 rounded-2xl shadow-md font-poppins mb-6">
            <h2 className="text-lg font-semibold text-darkTextColor mb-4">
              Delivery Options
            </h2>
            <div className="flex flex-col md:flex-row gap-4">
              {/* Create/Update Address */}
              <div className="flex-1 border border-cool-gray-200 rounded-md p-4 hover:border-primary transition-colors">
                <Link
                  to="/user/addressbook"
                  state={{ PageTitle: "Address" }}
                  className="flex items-center gap-2"
                >
                  <UilUserLocation className="w-5 h-5 text-orange-600" />
                  Create / Update Address(s)
                </Link>
              </div>

              {/* Deliver to my address */}
              <label className="flex-1 flex items-center gap-3 border border-cool-gray-200 rounded-md p-4 hover:border-primary cursor-pointer transition-colors">
                <input
                  type="radio"
                  name="deliveryOption"
                  value="delivery"
                  checked={deliveryOption === "delivery"}
                  onChange={handleDeliveryOptionChange}
                />
                <span>Deliver to my address</span>
              </label>
              {/* Direct Mosque Delivery */}
              <label className="flex-1 flex items-center gap-3 border border-cool-gray-200 rounded-md p-4 hover:border-primary cursor-pointer transition-colors">
                <input
                  type="radio"
                  name="deliveryOption"
                  value="mosque"
                  checked={deliveryOption === "mosque"}
                  onChange={handleDeliveryOptionChange}
                />
                <span className="relative text-primary font-poppins font-semibold">
                  Direct Mosque Delivery
                  {deliveryOption !== "mosque" && (
                    <>
                      <span className="absolute -top-2 -right-3 w-3 h-3 bg-red-500 rounded-full animate-ping"></span>
                      <span className="absolute -top-2 -right-3 w-3 h-3 bg-red-500 rounded-full"></span>
                    </>
                  )}
                </span>
              </label>
            </div>

            {/* Donate Water Message out of flex  */}
            {donateWater && (
              <div className="mt-2 flex items-center rounded-md p-2 bg-yellow-liner text-yellow-800">
                <UiMosque
                  className="w-5 h-5 mr-3 flex-shrink-0"
                  fillColor="#0A32A0"
                />
                <span className="text-sm text-C12 ml-2">
                  Your order will be donated to one of your nearest mosques and
                  you will be notified. Alternatively, you can click on{" "}
                  <strong>Mosque Orders</strong> in the left menu.
                </span>
              </div>
            )}
          </div>

          <div className="mt-3">
            {deliveryOption === "delivery" && (
              <div>
                <CheckoutDL
                  locations={locations}
                  onSelect={handleLocationSelect}
                  selectedLocation={selectedLocation}
                />
                {selectedLocation && (
                  <div className="mt-4 p-4 border rounded bg-gray-50">
                    <h3 className="font-semibold">You selected:</h3>
                    <p>
                      {selectedLocation.locationLabel} -{" "}
                      {selectedLocation.cityName}
                    </p>
                  </div>
                )}
              </div>
            )}
            {deliveryOption === "mosque" && (
              <div>
                <MosqueSelection myMosqueSelection={handleMosqueSelection} />
                {selectedMosque && (
                  <div className="mt-4 p-4 border rounded bg-green-50">
                    <h3 className="font-semibold">
                      You selected the mosque to be delivered:
                    </h3>
                    <p>
                      {selectedMosque.MosqueID} - {selectedMosque.MosqueName}
                    </p>
                  </div>
                )}
              </div>
            )}
            <div className="sr-only">
              <h3 className="text-cool-gray-400 font-poppins text-base mt-2">
                Selected Mosque Details:
              </h3>
              <p className="text-cool-gray-400 font-poppins text-C12">
                {selectedMosque
                  ? `${
                      selectedMosque.MosqueID === null
                        ? ""
                        : selectedMosque.MosqueID
                    } ${
                      selectedMosque.MosqueName === null
                        ? ""
                        : selectedMosque.MosqueName
                    }`
                  : "No Mosque Selected"}
              </p>
            </div>
          </div>
        </div>
        <div className="max-w-7xl">
          {cartCount > 0 ? (
            <div>
              {cartItems.map((product) => (
                <CheckoutItemCard
                  key={product.id}
                  product={{
                    ...product,
                    price: getDisplayPrice(product),
                  }}
                />
              ))}
              <div className="">
                <h2 className="sr-only">Order summary</h2>
                <dl className="mt-10 space-y-6 text-sm font-medium text-gray-500">
                  <div className="flex justify-between">
                    <dt>Subtotal</dt>
                    <dd className="text-gray-900">
                      {cartItems
                        .reduce(
                          (sum, item) =>
                            sum +
                            getRawPrice(item) * parseInt(item.quantity || 1),
                          0
                        )
                        .toFixed(2)}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>Taxes</dt>
                    <dd className="text-gray-900">
                      {(
                        cartItems.reduce(
                          (sum, item) =>
                            sum +
                            calculateTaxAmount(
                              getRawPrice(item),
                              item.quantity || 1,
                              0.05
                            ),
                          0
                        ) || 0
                      ).toFixed(2)}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>Shipping</dt>
                    <dd className="text-C12 text-red-500 max-w-20 w-40 font-poppins">
                      {cartGTotal < 75 ? `Minimum order 75 AED` : ""}
                    </dd>
                    <dd className="text-gray-900 ">
                      {shippingCharges.toFixed(2)}
                    </dd>
                  </div>
                  <div className="flex justify-between border-t border-cool-gray-400 pt-6 text-cool-gray-700">
                    <dt className="text-base">Total</dt>
                    <dd className="text-base">{cartGTotal.toFixed(2)}</dd>
                  </div>
                  {fiveGallonNotice && (
                    <div className="flex justify-start border-t border-cool-gray-400 pt-6 text-cool-gray-700">
                      <dt className="text-C12 bg-red-400 text-textWhite p-2">
                        Note:
                      </dt>
                      <dd className="text-C11 bg-yellow text-red-600 p-2">
                        If you're a new customer OR don't have empty bottles for
                        exchange, a refundable security deposit of 20 AED per
                        bottle is required for 5 Gallon Bottles.
                      </dd>
                    </div>
                  )}
                </dl>
                <div className="mx-auto w-full max-w-lg mt-4">
                  {cartGTotal < 75 ? (
                    <button
                      type="button"
                      disabled
                      className="flex w-full items-center justify-center rounded-md mt-5
                    border border-transparent cursor-not-allowed
                    bg-darkLight hover:bg-darkLight
                    px-6 py-3 text-base font-medium text-textWhite hover:text-cool-gray-700 shadow-sm "
                    >
                      Pay Your Order {cartGTotal.toFixed(2)} AED
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSubmit}
                      className="flex w-full items-center justify-center rounded-md mt-5
                                border border-transparent 
                                bg-secondary hover:bg-secondaryHover
                                px-6 py-3 text-base font-medium text-textWhite hover:text-cool-gray-700 shadow-sm "
                    >
                      <span className="">
                        Pay Your Order {cartGTotal.toFixed(2)} AED
                      </span>
                    </button>
                  )}
                  <div className="flex mt-8 justify-center md:justify-start">
                    <Link
                      to="/productsrange"
                      type="button"
                      state={{ PageTitle: "Shop" }}
                      className="inline-flex items-center justify-center w-full px-5 py-3 mr-3 text-base font-medium text-center 
                     text-primary rounded-md border border-1  hover:bg-secondaryVeryLight focus:ring-4 focus:ring-primary-300
                      dark:focus:ring-primaryLight"
                    >
                      <ShoppingBagIcon
                        width={24}
                        height={24}
                        className="mr-4 text-yellow"
                      />
                      Continue to Shop or Modify Your order
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="max-w-md">
              <span className="text-2xl text-center items-center justify-center">
                Your Cart is empty, please{" "}
                <Link
                  to="/productsrange"
                  type="button"
                  state={{ PageTitle: "Shop" }}
                  className="font-medium text-base text-primary hover:text-primaryHover mt-5 items-center justify-center "
                >
                  Continue to Shop
                  <span aria-hidden="true"> &rarr;</span>
                </Link>
              </span>
              <img src="../../images/emptycart.jpg" className="" alt="" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
